import React, {FC, useCallback, useContext, useMemo, useRef, useState} from "react";
import {DownloadOutlined, EyeOutlined} from "@ant-design/icons";
import {
    EventFlowTableColumnDefinition,
    RenderAntdGraphQLList
} from "../../Components/AdminModalForm";
import {useTranslation} from "react-i18next";
import {
    ITenantFormLayout,
    TextFieldElement,
    ITenantDeleting,
    ITenantUpdating
} from "@ib-tehnologije/react-widgets";
import {ADMIN_MAX_PAGE_SIZE} from "./adminPaging";
import {useNavigate, useParams} from "react-router-dom";
import {
    EventParticipantDeleteDtoInput,
    EventParticipantReturnDto,
    EventParticipantReturnDtoFilterInput,
    EventParticipantReturnDtoSortInput,
    EventParticipantStatusFilterDtoInput,
    EventParticipantUpdateDtoInput,
    EventParticipantsListQueryVariables,
    SortEnumType,
    useDeleteEventParticipantMutation,
    useEventParticipantCockpitRegistrationsQuery,
    useEventParticipantsListQuery,
    useEventParticipantsTotalQuery,
    useUpdateEventParticipantMutation
} from "../../GraphQL/generated/graphql";
import {Button, Input, Select, Space, Tooltip, Typography, App} from "antd";
import type {SelectProps} from "antd";
import {eventParticipantRoute} from "./eventWorkspaceRoutes";
import {
    canGenerateCertificateForRegistration,
    operationalPaymentStatusTone
} from "./participantOperationsHelpers";
import {AuthContext} from "../../Contexts/AuthContext";
import {OrganizerRegistrationModal} from "./OrganizerRegistrationModal";
import "./ParticipantsPage.css";
import {humanizeEnumValue, normalizeEnumKey} from "../../i18n/labelHelpers";
import {authorizationHeaders} from "../../api/authorization";
import {downloadResponseFile} from "../../downloadFile";
import {StatusTag} from "../../Components/ui/StatusTag";
import type {StatusTone} from "../../Components/ui/StatusTag";
import {PageHeader} from "../../Components/ui";

type ParticipantCockpitRegistration = {
    id: number;
    eventId: number;
    participantId: number;
    status?: string | null;
    rejectionReason?: string | null;
    hasAccommodationReservation?: boolean | null;
    accommodationReservationCount?: number | null;
    currentParticipationOfferNumber?: string | null;
    currentParticipationOfferStatus?: string | null;
    currentParticipationOfferPaymentStatus?: string | null;
    currentAccommodationOfferNumber?: string | null;
    currentAccommodationOfferStatus?: string | null;
    currentAccommodationOfferPaymentStatus?: string | null;
    activeRegistrationCodeCount?: number | null;
    revokedRegistrationCodeCount?: number | null;
    hasCertificates?: boolean | null;
    certificateCount?: number | null;
};

type ParticipantFilterState = {
    search: string;
    registrationStatuses: string[];
    feeStatuses: string[];
    accommodationStatuses: string[];
    certificateStatuses: string[];
    qrStatuses: string[];
};

const NO_REGISTRATION_FILTER = "NO_REGISTRATION";
const NO_FEE_OFFER_FILTER = "NO_FEE_OFFER";
const ACCOMMODATION_RESERVED_FILTER = "ACCOMMODATION_RESERVED";
const NO_ACCOMMODATION_OFFER_FILTER = "NO_ACCOMMODATION_OFFER";
const CERTIFICATE_ISSUED_FILTER = "CERTIFICATE_ISSUED";
const CERTIFICATE_READY_FILTER = "CERTIFICATE_READY";
const CERTIFICATE_NOT_READY_FILTER = "CERTIFICATE_NOT_READY";
const QR_ACTIVE_FILTER = "QR_ACTIVE";
const QR_ELIGIBLE_FILTER = "QR_ELIGIBLE";
const QR_NOT_READY_FILTER = "QR_NOT_READY";
// GraphQL enum values; the server applies every filter over all participants of the event.
const REGISTRATION_STATUS_VALUES = ["PRIJAVLJEN", "POZVAN", "REGISTRIRAN", "POTVRDEN", "ODBIJEN"];
const PAYMENT_STATUS_VALUES = ["UNPAID", "PENDING", "PAID", "MANUALLY_RECONCILED", "FAILED", "CANCELLED", "REFUNDED"];

const createDefaultParticipantFilters = (): ParticipantFilterState => ({
    search: "",
    registrationStatuses: [],
    feeStatuses: [],
    accommodationStatuses: [],
    certificateStatuses: [],
    qrStatuses: []
});

const registrationStatusTone = (status?: string | null): StatusTone => {
    switch (normalizeEnumKey(status)) {
    case "REGISTRIRAN":
    case "POTVRDEN":
        return "success";
    case "POZVAN":
        return "info";
    case "PRIJAVLJEN":
        return "warning";
    case "ODBIJEN":
        return "error";
    default:
        return "neutral";
    }
};

const registrationStatusRank = (status?: string | null) => {
    switch (normalizeEnumKey(status)) {
    // Lifecycle order, same as EventParticipantStatusQuery: Potvrđen is the final state.
    case "POTVRDEN":
        return 50;
    case "REGISTRIRAN":
        return 45;
    case "POZVAN":
        return 40;
    case "PRIJAVLJEN":
        return 30;
    case "ODBIJEN":
        return 10;
    default:
        return 0;
    }
};

const primaryRegistration = (registrations: ParticipantCockpitRegistration[]) =>
    [...registrations].sort((left, right) => {
        const rankDiff = registrationStatusRank(right.status) - registrationStatusRank(left.status);
        return rankDiff || right.id - left.id;
    })[0];

// Equal sort keys need a stable tie-breaker, otherwise rows can repeat or go missing between pages.
const withIdTieBreaker = (
    order?: EventParticipantReturnDtoSortInput | EventParticipantReturnDtoSortInput[] | null
): EventParticipantReturnDtoSortInput[] | undefined => {
    const items = (Array.isArray(order) ? order : order ? [order] : [])
        .filter(item => item && Object.keys(item).length > 0);

    if (items.length === 0) {
        return undefined;
    }

    return items.some(item => "id" in item) ? items : [...items, {id: SortEnumType.Asc}];
};

const renderCenteredStatus = (status: React.ReactNode) => (
    <div className="participants-page__status-cell">{status}</div>
);

const buildParticipantCsvDownloadUrl = (eventId: number) =>
    `/api/events/${eventId}/participants/export/csv`;

export const ParticipantsPage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {auth} = useContext(AuthContext);
    const navigate = useNavigate();
    const {eventId: routeEventId} = useParams();
    const [filters, setFilters] = useState<ParticipantFilterState>(() => createDefaultParticipantFilters());
    const [exportingParticipantsCsv, setExportingParticipantsCsv] = useState(false);
    const [registrationOpen, setRegistrationOpen] = useState(false);

    const eventId = routeEventId;
    const numericEventId = useMemo(() => {
        const parsed = Number(eventId);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
    }, [eventId]);

    const buildWhere = useCallback((term: string): EventParticipantReturnDtoFilterInput => ({
        and: [
            ...(numericEventId ? [{eventId: {eq: numericEventId}}] : []),
            ...(term ? [{
                or: [
                    {firstName: {contains: term}},
                    {lastName: {contains: term}},
                    {email: {contains: term}},
                    {company: {contains: term}}
                ]
            }] : [])
        ]
    }), [numericEventId]);

    const updateFilters = useCallback((patch: Partial<ParticipantFilterState>) => {
        setFilters(current => ({
            ...current,
            ...patch
        }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(createDefaultParticipantFilters());
    }, []);

    const hasActiveFilters = useMemo(() => filters.search.trim().length > 0
        || filters.registrationStatuses.length > 0
        || filters.feeStatuses.length > 0
        || filters.accommodationStatuses.length > 0
        || filters.certificateStatuses.length > 0
        || filters.qrStatuses.length > 0, [filters]);
    const statusFilter = useMemo<EventParticipantStatusFilterDtoInput | undefined>(() => {
        const selected: EventParticipantStatusFilterDtoInput = {
            ...(filters.registrationStatuses.length ? {registrationStatuses: filters.registrationStatuses} : {}),
            ...(filters.feeStatuses.length ? {feeStatuses: filters.feeStatuses} : {}),
            ...(filters.accommodationStatuses.length ? {accommodationStatuses: filters.accommodationStatuses} : {}),
            ...(filters.certificateStatuses.length ? {certificateStatuses: filters.certificateStatuses} : {}),
            ...(filters.qrStatuses.length ? {qrStatuses: filters.qrStatuses} : {})
        };

        return Object.keys(selected).length > 0 ? selected : undefined;
    }, [filters]);

    const [order, setOrder] = useState<EventParticipantReturnDtoSortInput[] | undefined>();
    // The list widget can hold an older refetch callback, so compare against a ref, not the render's state.
    const orderRef = useRef(order);
    const participantsQuery = useEventParticipantsListQuery({
        variables: {
            take: ADMIN_MAX_PAGE_SIZE,
            skip: 0,
            where: buildWhere(filters.search.trim()),
            statusFilter,
            order
        },
        notifyOnNetworkStatusChange: false,
        skip: !numericEventId
    });
    const participantsTotalQuery = useEventParticipantsTotalQuery({
        variables: {where: buildWhere("")},
        skip: !numericEventId || !hasActiveFilters
    });
    // Column sorts run on the server; the order lives in the query variables so it survives filter and search changes.
    const participantsListQuery = useMemo(() => ({
        ...participantsQuery,
        refetch: (variables?: Partial<EventParticipantsListQueryVariables>) => {
            if (!variables || !("order" in variables)) {
                return participantsQuery.refetch(variables);
            }

            const nextOrder = withIdTieBreaker(variables.order);
            if (JSON.stringify(nextOrder) !== JSON.stringify(orderRef.current)) {
                // Changing the variables refetches from the first page.
                orderRef.current = nextOrder;
                setOrder(nextOrder);
                return Promise.resolve({data: participantsQuery.data});
            }

            return participantsQuery.refetch({...variables, order: nextOrder});
        }
    }), [participantsQuery]);
    const visibleParticipants = useMemo(() => (participantsQuery.data?.eventParticipants?.items ?? [])
        .filter((participant): participant is EventParticipantReturnDto => !!participant),
    [participantsQuery.data?.eventParticipants?.items]);
    const visibleParticipantIds = useMemo(() => visibleParticipants
        .map(participant => participant.id), [visibleParticipants]);
    const hasVisibleParticipantIds = visibleParticipantIds.length > 0;
    const registrationsQuery = useEventParticipantCockpitRegistrationsQuery({
        variables: numericEventId && hasVisibleParticipantIds ? {
            take: ADMIN_MAX_PAGE_SIZE,
            skip: 0,
            order: [{id: SortEnumType.Desc}],
            where: {
                and: [
                    {eventId: {eq: numericEventId}},
                    {participantId: {in: visibleParticipantIds}}
                ]
            }
        } : undefined,
        skip: !numericEventId || !hasVisibleParticipantIds,
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: false
    });

    const getParticipantDisplayName = useCallback((participant: EventParticipantReturnDto) =>
        `${participant.firstName} ${participant.lastName}`.trim()
        || participant.email?.trim()
        || t("participant", {defaultValue: "Participant"}), [t]);

    const openParticipantDetailPage = useCallback((participant: EventParticipantReturnDto) => {
        navigate(
            eventParticipantRoute(participant.eventId, participant.id),
            {
                state: {
                    fallbackName: getParticipantDisplayName(participant)
                }
            });
    }, [getParticipantDisplayName, navigate]);

    const refetchRegistrationSnapshot = useCallback(async () => {
        if (!numericEventId || !hasVisibleParticipantIds) {
            return;
        }

        await registrationsQuery.refetch();
    }, [hasVisibleParticipantIds, numericEventId, registrationsQuery]);
    const registrationsByParticipantId = useMemo(() => {
        const grouped = new Map<number, ParticipantCockpitRegistration[]>();

        for (const registration of registrationsQuery.data?.eventRegistrations?.items ?? []) {
            if (!registration) {
                continue;
            }

            const registrations = grouped.get(registration.participantId) ?? [];
            registrations.push(registration);
            grouped.set(registration.participantId, registrations);
        }

        return grouped;
    }, [registrationsQuery.data?.eventRegistrations?.items]);

    const renderStatusLabel = useCallback((value?: string | null) =>
        value ? t(`enums:${value}`, {defaultValue: humanizeEnumValue(value)}) : "-", [t]);

    const renderPaymentStatusBadge = useCallback((
        status?: string | null,
        emptyLabel?: string
    ) => {
        if (!status) {
            return <StatusTag>{emptyLabel ?? t("not-available", {defaultValue: "Unavailable"})}</StatusTag>;
        }

        return <StatusTag tone={operationalPaymentStatusTone(status)}>{renderStatusLabel(status)}</StatusTag>;
    }, [renderStatusLabel, t]);

    const renderCertificateStatusBadge = useCallback((registrations: ParticipantCockpitRegistration[]) => {
        const certificateCount = registrations.reduce((total, registration) => total + Number(registration.certificateCount ?? 0), 0);
        const eligibleCount = registrations.filter(registration => canGenerateCertificateForRegistration(registration.status)).length;

        if (certificateCount > 0) {
            return <StatusTag tone="success">{t("certificate-issued", {defaultValue: "Issued"})}</StatusTag>;
        }

        if (eligibleCount > 0) {
            return <StatusTag tone="info">{t("certificate-ready", {defaultValue: "Ready"})}</StatusTag>;
        }

        return <StatusTag>{t("certificate-not-ready", {defaultValue: "Unready"})}</StatusTag>;
    }, [t]);

    const renderQrStatusBadge = useCallback((registrations: ParticipantCockpitRegistration[]) => {
        const activeCodeCount = registrations.reduce((total, registration) => total + Number(registration.activeRegistrationCodeCount ?? 0), 0);
        const revokedCodeCount = registrations.reduce((total, registration) => total + Number(registration.revokedRegistrationCodeCount ?? 0), 0);
        const eligibleCount = registrations.filter(registration =>
            ["PRIJAVLJEN", "POZVAN"].includes(normalizeEnumKey(registration.status))).length;

        if (activeCodeCount > 0) {
            return (
                <Space size={4} wrap>
                    <StatusTag tone="success">{t("qr-active", {defaultValue: "Active"})}</StatusTag>
                    {revokedCodeCount > 0 ? <StatusTag>{t("qr-revoked-count", {defaultValue: "Revoked"})}</StatusTag> : null}
                </Space>
            );
        }

        if (eligibleCount > 0) {
            return <StatusTag tone="info">{t("qr-eligible", {defaultValue: "Ready"})}</StatusTag>;
        }

        return <StatusTag>{t("qr-not-ready", {defaultValue: "Unready"})}</StatusTag>;
    }, [t]);

    const getParticipantRegistrations = useCallback((row: EventParticipantReturnDto) =>
        registrationsByParticipantId.get(row.id) ?? [], [registrationsByParticipantId]);
    const registrationStatusOptions = useMemo<SelectProps["options"]>(() => [
        ...REGISTRATION_STATUS_VALUES.map(value => ({label: renderStatusLabel(value), value})),
        {label: t("no-registration", {defaultValue: "None"}), value: NO_REGISTRATION_FILTER}
    ], [renderStatusLabel, t]);
    const feeStatusOptions = useMemo<SelectProps["options"]>(() => [
        ...PAYMENT_STATUS_VALUES.map(value => ({label: renderStatusLabel(value), value})),
        {label: t("no-fee-offer", {defaultValue: "None"}), value: NO_FEE_OFFER_FILTER}
    ], [renderStatusLabel, t]);
    const accommodationStatusOptions = useMemo<SelectProps["options"]>(() => [
        {label: t("accommodation-reserved", {defaultValue: "Reserved"}), value: ACCOMMODATION_RESERVED_FILTER},
        ...PAYMENT_STATUS_VALUES.map(value => ({label: renderStatusLabel(value), value})),
        {label: t("no-accommodation-offer", {defaultValue: "None"}), value: NO_ACCOMMODATION_OFFER_FILTER}
    ], [renderStatusLabel, t]);
    const certificateStatusOptions = useMemo<SelectProps["options"]>(() => [
        {label: t("certificate-issued-filter", {defaultValue: "Issued"}), value: CERTIFICATE_ISSUED_FILTER},
        {label: t("certificate-ready", {defaultValue: "Ready"}), value: CERTIFICATE_READY_FILTER},
        {label: t("certificate-not-ready", {defaultValue: "Unready"}), value: CERTIFICATE_NOT_READY_FILTER}
    ], [t]);
    const qrStatusOptions = useMemo<SelectProps["options"]>(() => [
        {label: t("qr-active", {defaultValue: "Active"}), value: QR_ACTIVE_FILTER},
        {label: t("qr-eligible", {defaultValue: "Ready"}), value: QR_ELIGIBLE_FILTER},
        {label: t("qr-not-ready", {defaultValue: "Unready"}), value: QR_NOT_READY_FILTER}
    ], [t]);

    const columns = useMemo<EventFlowTableColumnDefinition<EventParticipantReturnDto>[]>(() => [
        {
            columnId: "firstName",
            title: t("participant", {defaultValue: "Participant"}),
            width: 220,
            sorter: true,
            renderCell: (row) => {
                const name = getParticipantDisplayName(row);

                return (
                    <Space className="participants-page__participant-cell" direction="vertical" size={2}>
                        <Typography.Text strong>{name}</Typography.Text>
                        {row.company ? <Typography.Text type="secondary">{row.company}</Typography.Text> : null}
                    </Space>
                );
            }
        },
        {
            columnId: "registrationStatusSortKey",
            title: t("registration-status", {defaultValue: "Registration status"}),
            sorter: true,
            renderCell: (row) => {
                const registrations = getParticipantRegistrations(row);
                const registration = primaryRegistration(registrations);

                if (!registration) {
                    return renderCenteredStatus(<StatusTag>{t("no-registration", {defaultValue: "None"})}</StatusTag>);
                }

                return renderCenteredStatus(
                    <StatusTag tone={registrationStatusTone(registration.status)}>{renderStatusLabel(registration.status)}</StatusTag>
                );
            }
        },
        {
            columnId: "feeStatusSortKey",
            title: t("fee", {defaultValue: "Fee"}),
            sorter: true,
            renderCell: (row) => {
                const registrations = getParticipantRegistrations(row);
                const registration = primaryRegistration(registrations);

                return renderCenteredStatus(
                    renderPaymentStatusBadge(
                        registration?.currentParticipationOfferPaymentStatus,
                        t("no-fee-offer", {defaultValue: "None"})
                    )
                );
            }
        },
        {
            columnId: "accommodationStatusSortKey",
            title: t("accommodation", {defaultValue: "Accommodation"}),
            sorter: true,
            renderCell: (row) => {
                const registrations = getParticipantRegistrations(row);
                const registration = primaryRegistration(registrations);

                if (registration?.hasAccommodationReservation && !registration.currentAccommodationOfferPaymentStatus) {
                    return renderCenteredStatus(
                        <StatusTag tone="info">{t("accommodation-reserved", {defaultValue: "Reserved"})}</StatusTag>
                    );
                }

                return renderCenteredStatus(
                    renderPaymentStatusBadge(
                        registration?.currentAccommodationOfferPaymentStatus,
                        t("no-accommodation-offer", {defaultValue: "None"})
                    )
                );
            }
        },
        {
            columnId: "certificateStatusSortKey",
            title: t("certificate", {defaultValue: "Certificate"}),
            sorter: true,
            renderCell: (row) => {
                const registrations = getParticipantRegistrations(row);
                return renderCenteredStatus(renderCertificateStatusBadge(registrations));
            }
        },
        {
            columnId: "qrStatusSortKey",
            title: t("qr-invitation", {defaultValue: "QR invitation"}),
            sorter: true,
            renderCell: (row) => {
                const registrations = getParticipantRegistrations(row);
                return renderCenteredStatus(renderQrStatusBadge(registrations));
            }
        },
        {
            columnId: "actions",
            title: t("row-actions", {defaultValue: "Row actions"}),
            renderCell: (row) => (
                <Tooltip title={t("open-participant-cockpit", {defaultValue: "Open participant detail"})}>
                    <Button
                        size="small"
                        icon={<EyeOutlined/>}
                        onClick={(event) => {
                            event.stopPropagation();
                            openParticipantDetailPage(row);
                        }}
                    >
                        {t("open-cockpit", {defaultValue: "Open detail"})}
                    </Button>
                </Tooltip>
            )
        }
    ], [
        getParticipantDisplayName,
        getParticipantRegistrations,
        openParticipantDetailPage,
        renderCertificateStatusBadge,
        renderPaymentStatusBadge,
        renderQrStatusBadge,
        renderStatusLabel,
        t
    ]);

    const formDefinition = useMemo<ITenantFormLayout<EventParticipantUpdateDtoInput>>(() => ({
        firstName: {translationKey: "first-name", type: new TextFieldElement({})},
        lastName: {translationKey: "last-name", type: new TextFieldElement({})},
        email: {translationKey: "email", type: new TextFieldElement({inputProps: {type: "email"}})},
        phoneNumber: {translationKey: "phone", type: new TextFieldElement({})},
        company: {translationKey: "company", type: new TextFieldElement({})},
        linkedInUrl: {translationKey: "linkedin-url", type: new TextFieldElement({inputProps: {type: "url"}})}
    } as any), []);

    const [deleteParticipant] = useDeleteEventParticipantMutation();
    const [updateParticipant, {loading: updating}] = useUpdateEventParticipantMutation();
    const participantsLoaded = !!participantsQuery.data?.eventParticipants;
    const registrationsLoaded = !!registrationsQuery.data?.eventRegistrations;
    const shouldWaitForInitialRegistrations = hasVisibleParticipantIds && !registrationsLoaded;
    const isParticipantsListLoading = updating
        || (participantsQuery.loading && !participantsLoaded)
        || (registrationsQuery.loading && shouldWaitForInitialRegistrations);
    const handleExportParticipantsCsv = useCallback(async () => {
        if (!numericEventId) {
            return;
        }

        setExportingParticipantsCsv(true);
        try {
            const response = await fetch(buildParticipantCsvDownloadUrl(numericEventId), {
                headers: authorizationHeaders(auth.token)
            });

            if (!response.ok) {
                throw new Error(`CSV export failed with status ${response.status}`);
            }

            await downloadResponseFile(response, `event-${numericEventId}-participants.csv`);
            message.success(t("participants.bulk.export-csv-success", {
                defaultValue: "CSV export downloaded."
            }));
        } catch {
            message.error(t("participants.bulk.export-csv-error", {
                defaultValue: "CSV export failed. Try again."
            }));
        } finally {
            setExportingParticipantsCsv(false);
        }
    }, [message, auth.token, numericEventId, t]);

    const participantActionButtons = useMemo(() => [
        {
            type: "primary" as const,
            children: t("cta.new-participant", {defaultValue: "Add participant"}),
            onClick: () => setRegistrationOpen(true),
            disabled: !numericEventId
        },
        {
            icon: <DownloadOutlined/>,
            children: t("participants.bulk.export-csv", {defaultValue: "Export CSV"}),
            onClick: handleExportParticipantsCsv,
            disabled: !numericEventId,
            loading: exportingParticipantsCsv
        }
    ], [
        exportingParticipantsCsv,
        handleExportParticipantsCsv,
        numericEventId,
        t
    ]);

    const updatingConfig = useMemo<ITenantUpdating<any, EventParticipantReturnDto, EventParticipantUpdateDtoInput, "input">>(() => {
        const toDto = (values: any): EventParticipantUpdateDtoInput => ({
            id: Number(values.id),
            eventId: Number(eventId),
            firstName: values.firstName ?? "",
            lastName: values.lastName ?? "",
            company: values.company ?? "",
            phoneNumber: values.phoneNumber ?? "",
            email: values.email ?? "",
            linkedInUrl: values.linkedInUrl ?? "",
            additionalInfo: values.additionalInfo ?? ""
        });

        return {
            modelKey: "input",
            formDefinition,
            formDataToDto: toDto,
            fn: async ({variables}: any) => {
                const result = await updateParticipant({variables});
                await participantsQuery.refetch();
                await refetchRegistrationSnapshot();
                return result;
            },
            selectionToBaseFormData: (item) => ({
                ...item,
                eventId: item?.eventId
            })
        };
    }, [eventId, formDefinition, participantsQuery, refetchRegistrationSnapshot, updateParticipant]);

    const deletingConfig = useMemo<ITenantDeleting<any, EventParticipantReturnDto, EventParticipantDeleteDtoInput, "input">>(() => ({
        modelKey: "input",
        fn: deleteParticipant,
        selectionToDto: (item) => ({id: item.id})
    }), [deleteParticipant]);

    const participantList = (
        <RenderAntdGraphQLList
            tableData={participantsQuery.data?.eventParticipants}
            columns={columns}
            getRowId={(row) => row.id}
            getNotificationKey={getParticipantDisplayName}
            query={participantsListQuery}
            hideSearchInput
            pageStorageKey={"participants-list"}
            additionalInlineButtons={participantActionButtons}
            disableRowSelection={false}
            deleting={deletingConfig}
            deleteItemLabel={t("delete-target-participant", {defaultValue: "participant"})}
            updating={updatingConfig}
            style={{width: "100%"}}
            loading={isParticipantsListLoading}
        />
    );

    const filterSelectCommonProps = {
        allowClear: true,
        mode: "multiple" as const,
        maxTagCount: 1,
        showSearch: true,
        className: "participants-page__filter",
        notFoundContent: t("no-filter-options", {defaultValue: "No options"})
    };
    const shownParticipantCount = participantsQuery.data?.eventParticipants?.totalCount ?? 0;
    const eventParticipantCount = hasActiveFilters
        ? participantsTotalQuery.data?.eventParticipants?.totalCount ?? shownParticipantCount
        : shownParticipantCount;
    const resultSummary = t("participants.filter-result-summary", {
        defaultValue: "{{shown}} of {{total}} participants",
        shown: shownParticipantCount,
        total: eventParticipantCount
    });

    return (
        <div className="ati-workspace-page">
            <PageHeader title={t("participants", {defaultValue: "Participants"})} description={t("participant-cockpit.subtitle", {
                defaultValue: "Operate registration, payments, offers, QR, and certificates from one event list."
            })} level={2}/>
            <section className="participants-page__toolbar" aria-label={t("participants.filters", {defaultValue: "Participant filters"})}>
                <Input
                    allowClear
                    className="participants-page__search"
                    aria-label={t("participant-search", {defaultValue: "Search participants"})}
                    placeholder={t("participants.search-placeholder", {defaultValue: "Name, company, or email"})}
                    value={filters.search}
                    onChange={event => updateFilters({search: event.target.value})}
                />
                <Select
                    {...filterSelectCommonProps}
                    aria-label={t("registration-status", {defaultValue: "Registration status"})}
                    placeholder={t("registration-status", {defaultValue: "Registration status"})}
                    options={registrationStatusOptions}
                    value={filters.registrationStatuses}
                    onChange={values => updateFilters({registrationStatuses: values})}
                />
                <Select
                    {...filterSelectCommonProps}
                    aria-label={t("fee", {defaultValue: "Fee"})}
                    placeholder={t("fee", {defaultValue: "Fee"})}
                    options={feeStatusOptions}
                    value={filters.feeStatuses}
                    onChange={values => updateFilters({feeStatuses: values})}
                />
                <Select
                    {...filterSelectCommonProps}
                    aria-label={t("accommodation", {defaultValue: "Accommodation"})}
                    placeholder={t("accommodation", {defaultValue: "Accommodation"})}
                    options={accommodationStatusOptions}
                    value={filters.accommodationStatuses}
                    onChange={values => updateFilters({accommodationStatuses: values})}
                />
                <Select
                    {...filterSelectCommonProps}
                    aria-label={t("certificate", {defaultValue: "Certificate"})}
                    placeholder={t("certificate", {defaultValue: "Certificate"})}
                    options={certificateStatusOptions}
                    value={filters.certificateStatuses}
                    onChange={values => updateFilters({certificateStatuses: values})}
                />
                <Select
                    {...filterSelectCommonProps}
                    aria-label={t("qr-invitation", {defaultValue: "QR invitation"})}
                    placeholder={t("qr-invitation", {defaultValue: "QR invitation"})}
                    options={qrStatusOptions}
                    value={filters.qrStatuses}
                    onChange={values => updateFilters({qrStatuses: values})}
                />
                <div className="participants-page__toolbar-meta">
                    <Typography.Text type="secondary" className="participants-page__filter-summary">
                        {resultSummary}
                    </Typography.Text>
                    <Button
                        disabled={!hasActiveFilters}
                        onClick={clearFilters}
                    >
                        {t("clear-filter", {defaultValue: "Clear filter"})}
                    </Button>
                </div>
            </section>
            <div className="participants-page__table">
                {participantList}
            </div>
            {numericEventId ? (
                <OrganizerRegistrationModal
                    eventId={numericEventId}
                    open={registrationOpen}
                    onClose={() => setRegistrationOpen(false)}
                    onRegistered={response => {
                        setRegistrationOpen(false);
                        void participantsQuery.refetch();
                        if (response.participantId) {
                            navigate(eventParticipantRoute(numericEventId, response.participantId));
                        }
                    }}
                />
            ) : null}
        </div>
    );
};

export default ParticipantsPage;
